import { festivosCatalunya } from "../../../utils/fechasFestivas";
import { TYPE_COLORS } from "./absencesMonthUtils";
import { REQUEST_TYPE_LABEL } from "./vacationCalendarUtils";

const MONTH_NAMES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const WEEKDAY_SHORT = ["L", "M", "X", "J", "V", "S", "D"];

const HOLIDAY_COLOR = "#ef6b6b";
const VACATION_TEAL = "#1aae9f";
const CARRY_COLOR = "#fb8c00";

const TYPE_FILL = {
  ...TYPE_COLORS,
  vacation: VACATION_TEAL,
  vacation_carry: CARRY_COLOR,
};

const VISIBLE_STATUSES = new Set(["approved", "pending"]);

function carryCutoffForYear(year) {
  return `${year}-04-30`;
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toDateStr(year, month, day) {
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function todayDateStr() {
  const now = new Date();
  return toDateStr(now.getFullYear(), now.getMonth() + 1, now.getDate());
}

/** Monday-first weeks, including weekends. Trailing empty weeks are dropped. */
export function buildMonthWeeks(year, month) {
  const first = new Date(year, month - 1, 1);
  const startOffset = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const day = i - startOffset + 1;
    if (day < 1 || day > daysInMonth) {
      cells.push(null);
    } else {
      cells.push({ day, dateStr: toDateStr(year, month, day) });
    }
  }
  const weeks = [];
  for (let w = 0; w < 6; w += 1) {
    weeks.push(cells.slice(w * 7, w * 7 + 7));
  }
  while (weeks.length > 4 && weeks[weeks.length - 1].every((c) => !c)) {
    weeks.pop();
  }
  return weeks;
}

function expandRange(startDate, endDate) {
  const dates = [];
  const start = String(startDate || "").slice(0, 10);
  const end = String(endDate || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) {
    return dates;
  }
  const d = new Date(`${start}T12:00:00`);
  const last = new Date(`${end}T12:00:00`);
  while (d <= last) {
    dates.push(toDateStr(d.getFullYear(), d.getMonth() + 1, d.getDate()));
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

/**
 * Map ISO date → { kind, color, label, pending, striped, outline, fromCarry }.
 * Vacaciones hasta el 30 de abril = año anterior (consumidas este año).
 * Pending = círculo vacío, no el rayado del teletrabajo.
 * Absence (approved/pending) wins over public holiday.
 */
export function buildYearDayMarks(entries = [], year) {
  const holidays = festivosCatalunya(year);
  const marks = {};
  const carryCutoff = carryCutoffForYear(year);

  holidays.forEach((dateStr) => {
    marks[dateStr] = {
      kind: "holiday",
      color: HOLIDAY_COLOR,
      label: "Festivo",
      pending: false,
      striped: false,
      outline: false,
      fromCarry: false,
    };
  });

  (entries || []).forEach((entry) => {
    const status = entry.status || entry.Status;
    if (!VISIBLE_STATUSES.has(status)) return;
    const type = entry.requestType || entry.RequestType;
    const start = entry.startDate || entry.StartDate;
    const end = entry.endDate || entry.EndDate;
    const pending = status === "pending";
    expandRange(start, end).forEach((dateStr) => {
      if (!dateStr.startsWith(`${year}-`)) return;
      const fromCarry =
        type === "vacation" && !pending && dateStr <= carryCutoff;
      const kind = fromCarry ? "vacation_carry" : type || "vacation";
      marks[dateStr] = {
        kind,
        color: fromCarry ? CARRY_COLOR : TYPE_FILL[type] || VACATION_TEAL,
        label: fromCarry
          ? "Vacaciones año anterior"
          : REQUEST_TYPE_LABEL[type] || type,
        pending,
        striped: type === "remote_day",
        outline: pending,
        fromCarry,
      };
    });
  });

  return marks;
}

/** Días de vacaciones aprobadas del 1 ene al 30 abr (año anterior disfrutado este año). */
export function countCarryoverVacationDays(entries = [], year, userId) {
  const cutoff = carryCutoffForYear(year);
  let n = 0;
  (entries || []).forEach((entry) => {
    const status = entry.status || entry.Status;
    const type = entry.requestType || entry.RequestType;
    if (status !== "approved" || type !== "vacation") return;
    const uid = entry.userId ?? entry.UserId;
    if (userId != null && uid != null && Number(uid) !== Number(userId)) return;
    const start = entry.startDate || entry.StartDate;
    const end = entry.endDate || entry.EndDate;
    expandRange(start, end).forEach((dateStr) => {
      if (dateStr.startsWith(`${year}-`) && dateStr <= cutoff) n += 1;
    });
  });
  return n;
}

export {
  MONTH_NAMES,
  WEEKDAY_SHORT,
  HOLIDAY_COLOR,
  VACATION_TEAL,
  CARRY_COLOR,
  TYPE_FILL,
  todayDateStr,
};
